// ─────────────────────────────────────────────────────────────────────────────
// «Distribución por Fundamental», a ANCHO COMPLETO y con barras. Lo usan el tab Diario y el de
// Brillamos: es la misma pregunta —«¿por qué Fundamental se reconoce?»— y tenerla con dos aspectos
// distintos según la pestaña sería gratuito.
//
// DOS NIVELES (UAT 24-ago-2026). El Fundamental padre lleva su porcentaje total y, al pulsarlo, se
// despliegan CON SANGRÍA los Daily Habits por los que se le reconoció. El árbol lo arma ESTA pantalla,
// no el backend: los hábitos llegan planos y la relación hábito → Fundamental sale del catálogo
// (`dailyHabits[].fundamentalId`), porque AnalyticsService no lee la base de SurveyService (regla de
// oro #2) y mandar el árbol desde allí le obligaría a conocer un catálogo que no es suyo.
//
// 🔴 El catálogo es `useFundamentalsForReporting`, NO `useRecognitionCatalog`. Este bloque resuelve
// ids HISTÓRICOS: un Fundamental retirado ya no está en el catálogo activo, y con él la pantalla caía
// al `nombre` que manda el backend —que es el propio id— y pintaba GUIDs crudos en la gráfica (visto
// en QA el 24-ago). Es el mismo defecto que ya se corrigió en la columna «Top Fundamental» de Mi
// equipo, y el hook existía justo para esto.
//
// Los colores salen del catálogo (`getFundamentalColor`), igual que hace `NetworkTab`: no se inventa
// una paleta nueva ni se hardcodean hex por fundamental.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getFundamentalName, getFundamentalColor } from '@/api/recognition';
import { useFundamentalsForReporting } from '@/hooks/useFundamentalsForReporting';
import type { RankedEntryDto } from '@/api/recognition';

export interface DistribucionFundamentalProps {
  readonly entradas: readonly RankedEntryDto[];
  /** Total del periodo: denominador de los porcentajes y cifra de la tarjeta lateral. */
  readonly total: number;
  /**
   * Daily Habits del periodo, PLANOS. Opcional: sin ellos el bloque se queda en un solo nivel, que es
   * lo que hacía antes. No se inventa el desglose cuando el backend no lo manda.
   */
  readonly habitos?: readonly RankedEntryDto[];
  /**
   * Aspecto del bloque. Son DOS bloques distintos en la maqueta, no uno con dos tamaños:
   *
   *   · `completa` (por omisión) — la del tab Diario: barra gruesa, «% · conteo» y tarjeta lateral con
   *     el total del periodo.
   *   · `fina` — la del tab Brillamos: carril de 10 px, nombre CORTO a la izquierda y SOLO el
   *     porcentaje a la derecha. Sin tarjeta lateral, sin conteo.
   *
   * 🔴 Es una variante y no una reescritura A PROPÓSITO: el Diario ya está cerrado y validado, y
   * cambiar el estilo base lo movería. Las clases de la variante son propias; ninguna redefine las que
   * usa el Diario.
   */
  readonly variante?: 'completa' | 'fina';
}

interface Fila {
  readonly id: string;
  readonly nombre: string;
  readonly color: string;
  readonly count: number;
  readonly oculta: boolean;
  /** true si el id no está ni en el catálogo activo ni en el histórico: no se pudo resolver. */
  readonly desconocido: boolean;
  readonly habitos: readonly { id: string; nombre: string; count: number }[];
}

export function DistribucionFundamental({
  entradas, total, habitos = [], variante = 'completa',
}: DistribucionFundamentalProps) {
  const { t, i18n } = useTranslation('recognition');
  const { data: catalogo, isSuccess: catalogoCargado } = useFundamentalsForReporting();
  const lang = i18n.language.split('-')[0];
  const [abierto, setAbierto] = useState<string | null>(null);

  /** Fundamental → metadatos, y hábito → (nombre, fundamental padre). Del catálogo CON inactivos. */
  const { porFundamental, habitoPadre, nombreDeHabito } = useMemo(() => {
    const fundamentales = new Map<string, { nombre: string; color: string }>();
    const padre = new Map<string, string>();
    const nombres = new Map<string, string>();

    (catalogo ?? []).forEach((f, i) => {
      fundamentales.set(f.id, {
        // `getFundamentalName`/`getFundamentalColor` esperan el DTO de Reconocimiento; el item del
        // catálogo de reportería trae los mismos campos que ellos leen.
        nombre: getFundamentalName(f as never, lang),
        color: getFundamentalColor(f as never, i),
      });
      for (const h of f.dailyHabits ?? []) {
        padre.set(h.id, f.id);
        nombres.set(h.id, h.names?.[lang] ?? h.names?.en ?? h.code ?? h.id);
      }
    });

    return {
      porFundamental: fundamentales,
      habitoPadre: padre,
      nombreDeHabito: (id: string) => nombres.get(id) ?? id,
    };
  }, [catalogo, lang]);

  const filas: Fila[] = useMemo(() => entradas.map((e) => {
    const meta = porFundamental.get(e.id);
    const propios = habitos
      .filter((h) => habitoPadre.get(h.id) === e.id)
      .map((h) => ({ id: h.id, nombre: nombreDeHabito(h.id), count: h.count }));

    return {
      id: e.id,
      /*
       * Si no se resuelve NO se enseña el id: un GUID en una gráfica no dice nada y se lee como un
       * fallo de la pantalla.
       *
       * 🔴 Pero «no resuelve» tiene DOS causas y solo una permite afirmar algo:
       *   · el catálogo cargó y este id no está en él  → el Fundamental está retirado. Se dice.
       *   · el catálogo NO cargó (red caída, 401…)     → no se sabe nada de ningún id. Decir
       *     «retirado» ahí sería afirmar que el cliente retiró sus Fundamentales por un fallo de red.
       * Visto en local: sin catálogo, las CUATRO barras se rotulaban «Fundamental retirado».
       */
      nombre: meta?.nombre
        ?? (catalogoCargado ? t('reports.daily.retiredFundamental') : t('reports.daily.unresolvedFundamental')),
      color: meta?.color ?? 'var(--cp-slate, #94a3b8)',
      count: e.count,
      oculta: e.suprimido === true,
      // Solo cuenta como «retirado» —y por tanto dispara la leyenda— si el catálogo SÍ cargó.
      desconocido: meta === undefined && catalogoCargado,
      habitos: propios,
    };
  }), [entradas, habitos, porFundamental, habitoPadre, nombreDeHabito, catalogoCargado, t]);

  if (entradas.length === 0) {
    return <div className="rr-state">{t('reports.common.empty')}</div>;
  }

  // La barra se escala al MAYOR de la lista, no al total: con cuatro fundamentales repartidos, medir
  // contra el total dejaría todas las barras por debajo de un tercio del carril y la comparación —que
  // es para lo que está la barra— se perdería. El porcentaje sobre el total lo dice el rótulo.
  const mayor = Math.max(...filas.map((f) => f.count), 1);
  const hayRetirados = filas.some((f) => f.desconocido);

  function alternar(id: string) {
    setAbierto((actual) => (actual === id ? null : id));
  }

  const esFina = variante === 'fina';

  return (
    <div className={esFina ? 'rr-fund-dist rr-fund-dist--fina' : 'rr-fund-dist'}>
      <div className="rr-fund-dist-bars">
        {filas.map((fila) => {
          const pct = total > 0 ? Math.round((fila.count / total) * 100) : 0;
          const desplegable = !fila.oculta && fila.habitos.length > 0;
          const desplegado = abierto === fila.id;

          return (
            <div key={fila.id || fila.nombre}>
              <div
                className={desplegable ? 'rr-fund-dist-row rr-fund-dist-row--clickable' : 'rr-fund-dist-row'}
                role={desplegable ? 'button' : undefined}
                tabIndex={desplegable ? 0 : undefined}
                aria-expanded={desplegable ? desplegado : undefined}
                onClick={desplegable ? () => alternar(fila.id) : undefined}
                onKeyDown={desplegable ? (e: KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    alternar(fila.id);
                  }
                } : undefined}
              >
                <span className="rr-fund-dist-name">
                  {desplegable && (
                    <i
                      className={desplegado ? 'fas fa-chevron-down rr-fund-dist-caret' : 'fas fa-chevron-right rr-fund-dist-caret'}
                      aria-hidden="true"
                    />
                  )}
                  {fila.oculta ? t('reports.common.masked') : fila.nombre}
                  {fila.desconocido && !fila.oculta && <sup className="rr-fund-dist-mark">*</sup>}
                </span>
                <div className="rr-fund-dist-track">
                  {!fila.oculta && (
                    <div
                      className="rr-fund-dist-fill"
                      style={{
                        width: `${Math.max(2, (fila.count / mayor) * 100)}%`,
                        background: fila.color,
                      }}
                    />
                  )}
                </div>
                <span className="rr-fund-dist-value">
                  {/* La variante fina de la maqueta enseña SOLO el porcentaje: el conteo va en los
                      KPIs de arriba y repetirlo aquí solo estrecha el carril. */}
                  {fila.oculta ? '—' : (esFina ? `${pct}%` : `${pct}% · ${fila.count}`)}
                </span>
              </div>

              {/*
                Segundo nivel: los hábitos, con sangría. El porcentaje es sobre el TOTAL DEL PADRE, no
                sobre el del periodo: la pregunta al desplegar es «de este Fundamental, ¿por qué
                hábito?», y medirlo contra el periodo daría cifras diminutas que no responden a eso.
              */}
              {desplegado && fila.habitos.map((h) => {
                const pctHabito = fila.count > 0 ? Math.round((h.count / fila.count) * 100) : 0;
                return (
                  <div key={h.id} className="rr-fund-dist-row rr-fund-dist-row--child">
                    <span className="rr-fund-dist-name">{h.nombre}</span>
                    <div className="rr-fund-dist-track">
                      <div
                        className="rr-fund-dist-fill rr-fund-dist-fill--child"
                        style={{
                          width: `${Math.max(2, (h.count / Math.max(fila.count, 1)) * 100)}%`,
                          background: fila.color,
                        }}
                      />
                    </div>
                    <span className="rr-fund-dist-value">{`${pctHabito}% · ${h.count}`}</span>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/*
          Leyenda de los retirados. Se pinta SOLO si hay alguno: es una explicación, no una nota fija,
          y en el caso normal —todos los Fundamentales vigentes— no tiene nada que explicar.
        */}
        {hayRetirados && (
          <p className="rr-fund-dist-legend">
            <span className="rr-fund-dist-mark">*</span> {t('reports.daily.retiredFundamentalHint')}
          </p>
        )}
      </div>

      {/* Tarjeta lateral: SOLO en la variante completa (Diario). La maqueta de Brillamos no la lleva
          — ahí el total ya está en los KPIs de arriba de la pantalla. */}
      {!esFina && (
        <aside className="rr-fund-dist-total">
          <div className="rr-fund-dist-total-n">{total.toLocaleString(i18n.language)}</div>
          <div className="rr-fund-dist-total-l">{t('reports.daily.totalSentTitle')}</div>
          <p className="rr-fund-dist-total-h">{t('reports.daily.totalSentHint')}</p>
        </aside>
      )}
    </div>
  );
}
